import ScriptingEffect from './ScriptingEffect.js';
import {doc} from './documentation.js';
import {append, sublist, vectorAdd, len, car, cdr, map, nth} from './list.js';
import {search} from './search.js';
import {loop} from './loop.js';
import {sparse} from './sparse.js';
import {time} from './time.js';
import {resolution} from './resolution.js';
import {triggerAttack, triggerRelease} from './triggerAttackRelease.js';
import {getScriptForTitleSync} from 'decentralized/scripting/scripting.js';
import {add, multiply, mod} from './lazy.js';
import {print} from './log.js';
import {once} from './once.js';
import {counter} from './counter.js';
import {harmonize, strum} from './harmonize.js';
import {stretch} from './stretch.js';
import {setScriptVariables} from 'actions/index.js';
import {pipe} from './pipe.js';
import {send, receive} from './msg.js';
import {threshold} from './threshold.js';
import {delay} from './delay.js';
import {range} from './array.js';
import {poly} from './poly.js';
import {lte, gte} from './compare.js';
import {density} from './density.js';
import {scale} from './scale.js';
import {
    setMacro,
    choke,
    env1
,
    switchOutput,
    env2,
    env3,
    env4,
    s,
    notes,
    steps,
    rolling,
    reverse,
    p,
    prob,
    cond,
    set,
    get,
    patternSwitch,
    start,
    on,
    grab,
    grabTime,
    randomSelect,
    transpose,
    filter,
    trigger,
    vel,
    dur,
    chop,
    lazy
} from './lang.js';
import {repeat, beat, gate, shift} from './beat.js';
import {alt, altn, every} from './alt.js';
import {acc} from './accumulate.js';

class UserScriptEffect extends ScriptingEffect {
    constructor(track) {
        super("User Script", track);
        this.logs = [];
        this.code = "";
        this.clear();
        this.numberOfOutputs = 2;
    }

    isRealTime() { return true; }

    get documentation() {
        return [
            "Write scripts for transforming triggers, and create your own custom midi-effects."
        ];
    }

    getContext() {
        return {
            setMacro,
            env1,
            search,
            sparse,
            once,
            time,
            resolution,
            env2,
            engine: this.track.sequencerEngine,
            env3,
            env4,
            doc,
            choke,
            alt,
            altn,
            density,
            gte,
            lte,
            every,
            lazy,
            nth,
            scale,
            car,
            cdr,
            loop,
            len,
            sublist,
            vectorAdd,
            append,
            map,
            stretch,
            floor: Math.floor,
            round: Math.round,
            random: Math.random,
            print,
            acc,
            reverse,
            s,
            pipe,
            set,
            filter,
            trigger,
            harmonize,
            multiply,
            add,
            mod,
            counter,
            start,
            poly,
            receive,
            grab,
            on,
            patternSwitch,
            send,
            grabTime,
            steps,
            gate,
            range,
            shift,
            beat,
            notes,
            rolling,
            cond,
            get,
            repeat,
            prob,
            randomSelect,
            threshold,
            transpose,
            strum,
            triggerAttack,
            triggerRelease,
            load: (name) => this.loadScript(name),
            library: (name) => this.loadScript(name, ""),
            p,
            switchOutput,
            vel,
            delay,
            dur,
            chop,
            knob: (name, val, min=0, max=16) =>
                {
                    let v = this.newVar(name, val, min, max);
                    return () => {
                        let _var = this[name+'Parameter'];
                        let ret = _var.get();
                        return ret;
                    };
                },
        };
    }

    evalWithContext(str, func="pipe") {
        try {
            let pieces = str.split(";");
            let last = pieces[pieces.length - 1];
            let preamble = pieces.slice(0, pieces.length - 1).join(";");
            let code = "with(this) { " +
                preamble + "; return " + func + "(" + last + ")}";
            return (new Function(code)).call(this.getContext());
        } catch (e) {
            return undefined;
        }
    }

    loadScript(name, func="s") {
        // first synchronusly get the script w/ title=name
        let code = getScriptForTitleSync(name);

        // then evaluate
        let ctxt = this.evalWithContext(code, func);

        // return the ctxt
        if (func==="") {
            return ctxt;
        }
        return () => ctxt; 
    }

    hasSyntaxError(str) {
        return this.evalWithContext(str) === undefined;
    }

    eval(str) {
        let values = this.clearVariables();
        let myScript = this.evalWithContext(str);
        if (myScript === undefined) {
            this.hasError = true;
        } else {
            this.myScript = myScript;
            this.hasError = false;
            this.code = str;
        }

        for (let name in values) {
            if (this[name + 'Parameter']) {
                this[name + 'Parameter'].copyFrom(
                    values[name], this.track.sequencerEngine);
            }
        }
        this.updateVariablesRedux();
    }

    clear() {
        if (this.code === '') {
            return;
        }
        this.eval(this.code);
    }

    getCopy(track, ignoreId=true) {
        let fx = new UserScriptEffect(track);
        fx.fromJson(this.getJson(), ignoreId);
        return fx;
    }

    getJson() {
        return {
            ... super.getJson(),
            expanded: this.expanded,
            code: this.code
        };
    }

    fromJson(json, isCopy) {
        this.code = json.code;
        this.expanded = json.expanded;
        this.clear();
        super.fromJson(json, isCopy);
    }
}

export default UserScriptEffect;
